import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Download, RefreshCcw, X } from "lucide-react";
import { FIELD_TITLES, LABELS, SECTORS, SURVEYORS, shortLabel } from "../survey/questions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pendli-admin-token";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` });

const EMPTY_FILTERS = {
  surveyor: "",
  sector: "",
  street: "",
  date_from: "",
  date_to: "",
  walking_distance: "",
  pendli_interest: "",
  contact_permission: "",
  potential_host: "",
};

const Card = ({ label, value, sub, color = "text-amber", testId }) => (
  <div className="bg-amber-deep border border-amber/20 p-5" data-testid={testId}>
    <div className="text-xs font-bold tracking-widest uppercase text-cream/60 mb-1">{label}</div>
    <div className={`font-display text-3xl md:text-4xl font-extrabold ${color}`} style={{ letterSpacing: "-0.04em" }}>
      {value}
    </div>
    {sub && <div className="text-xs text-cream/50 mt-1">{sub}</div>}
  </div>
);

const Select = ({ label, value, onChange, options, testId }) => (
  <label className="block">
    <span className="block text-[10px] font-bold tracking-widest uppercase text-amber/70 mb-1.5">{label}</span>
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-9 bg-ink border border-amber/25 focus:border-amber text-sm text-cream px-2"
    >
      {options.map((o) => (
        <option key={o.v} value={o.v}>{o.l}</option>
      ))}
    </select>
  </label>
);

function Breakdown({ field, data }) {
  if (!data) return null;
  return (
    <div className="bg-amber-deep/30 border border-amber/15 p-5" data-testid={`breakdown-${field}`}>
      <h3 className="font-display text-base font-bold tracking-tight mb-1">{FIELD_TITLES[field]}</h3>
      <p className="text-[11px] text-cream/40 mb-4">Grunnlag: {data.base} svar</p>
      <div className="space-y-3">
        {data.items.map((it) => (
          <div key={it.value}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-cream/75">{shortLabel(field, it.value)}</span>
              <span className="text-amber font-bold">{it.count} · {it.pct}%</span>
            </div>
            <div className="h-2 bg-amber/10">
              <div className="h-full bg-amber" style={{ width: `${it.pct}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DetailModal({ row, onClose }) {
  useEffect(() => {
    if (!row) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [row, onClose]);

  if (!row) return null;
  const fields = [
    ["Dato", new Date(row.created_at).toLocaleString("nb-NO")],
    ["Kartlegger", LABELS.surveyor[row.surveyor]],
    ["Sektor", LABELS.sector[row.sector]],
    ["Gate", row.street],
    ...Object.keys(FIELD_TITLES).map((f) => [FIELD_TITLES[f], row[f] ? LABELS[f][row[f]] : "—"]),
    ["Annet hinder (fritekst)", row.main_barrier_other || "—"],
    ["Navn", row.contact_name || "—"],
    ["Telefon", row.contact_phone || "—"],
    ["E-post", row.contact_email || "—"],
    ["Avsluttet tidlig", row.early_exit_reason || "nei"],
    ["Lead-ID", row.lead_id || "—"],
    ["Svar-ID", row.id],
  ];
  return (
    <div
      className="fixed inset-0 z-50 bg-ink/90 backdrop-blur-sm flex items-start md:items-center justify-center p-3 md:p-6"
      data-testid="survey-detail-modal"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Undersøkelse"
    >
      <div
        className="relative w-full max-w-lg bg-ink border border-amber/30 max-h-[92vh] md:max-h-[85vh] flex flex-col my-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sticky header + close */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-4 bg-ink border-b border-amber/20 px-5 py-4">
          <div className="min-w-0">
            <h3 className="font-display text-xl md:text-2xl font-extrabold tracking-tight truncate">Undersøkelse</h3>
            <p className="text-xs text-cream/50 truncate">
              {LABELS.surveyor[row.surveyor]} · {LABELS.sector[row.sector]} · {row.street}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="survey-detail-close"
            aria-label="Lukk"
            className="shrink-0 w-11 h-11 flex items-center justify-center border border-amber/40 text-amber hover:bg-amber hover:text-ink transition-colors"
          >
            <X size={22} strokeWidth={2.5} />
          </button>
        </div>

        <dl className="space-y-3 overflow-y-auto px-5 py-5">
          {fields.map(([k, v]) => (
            <div key={k} className="flex gap-4 justify-between border-b border-amber/10 pb-2">
              <dt className="text-xs font-bold tracking-widest uppercase text-cream/50 shrink-0">{k}</dt>
              <dd className="text-sm text-cream text-right break-words">{v}</dd>
            </div>
          ))}
        </dl>

        <div className="border-t border-amber/20 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="survey-detail-close-bottom"
            className="w-full h-11 text-xs font-bold tracking-widest uppercase text-cream/60 hover:text-amber transition-colors"
          >
            Lukk
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminSurvey({ onUnauthorized }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);

  const setF = (k) => (v) => setFilters((p) => ({ ...p, [k]: v }));

  const params = useCallback(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/admin/survey/stats`, { headers: authHeaders(), params: params() }),
        axios.get(`${API}/admin/survey/responses`, { headers: authHeaders(), params: params() }),
      ]);
      setStats(s.data);
      setRows(r.data);
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      setError("Klarte ikke laste undersøkelsesdata.");
    } finally {
      setLoading(false);
    }
  }, [params, onUnauthorized]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/survey/responses.csv`, {
        headers: authHeaders(), params: params(), responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pendli-survey-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      setError("Klarte ikke laste ned CSV.");
    }
  };

  const isPotentialHost = (r) =>
    ["driveway", "garage", "other"].includes(r.parking_type) &&
    ["yes", "yes_if_easy"].includes(r.pendli_interest);

  return (
    <div data-testid="admin-survey">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter" style={{ letterSpacing: "-0.04em" }}>
            Dør-til-dør
          </h1>
          <p className="text-sm text-cream/50 mt-2">Markedsvalidering rundt Asker stasjon</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="survey-refresh" className="h-9 px-3 border border-amber/30 text-cream/80 hover:text-amber text-xs font-bold tracking-widest uppercase inline-flex items-center gap-1.5">
            <RefreshCcw size={13} /> Oppdater
          </button>
          <button onClick={downloadCsv} data-testid="survey-export-csv" className="h-9 px-4 bg-amber text-ink font-bold text-xs tracking-widest uppercase hover:bg-amber-hover inline-flex items-center gap-2">
            <Download size={14} /> Eksport CSV
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6" data-testid="survey-stats">
        <Card testId="stat-total" label="Totalt antall svar" value={`${stats?.total ?? 0} svar`} sub={`${stats?.no_parking ?? 0} uten plass`} />
        <Card testId="stat-parking" label="Har privat plass" value={stats?.with_parking ?? 0} sub={`${stats?.with_parking_pct ?? 0}% av alle svar`} />
        <Card testId="stat-empty" label="Tom nesten alltid" value={stats?.empty_almost_always ?? 0} sub={`${stats?.empty_almost_always_pct ?? 0}% av de med plass`} />
        <Card testId="stat-potential" label="Potensielle verter" value={stats?.potential_hosts ?? 0} sub={`${stats?.potential_hosts_pct ?? 0}% av de med plass`} color="text-orange" />
        <Card testId="stat-consent" label="Kan kontaktes" value={stats?.contact_permission ?? 0} sub={`${stats?.interested_with_contact ?? 0} interesserte med kontaktinfo`} color="text-cream" />
        <Card testId="stat-near" label="Innen 5 min · interessert" value={`${stats?.near_station_interested_pct ?? 0}%`} sub={`${stats?.near_station_interested ?? 0} av ${stats?.near_station ?? 0} nær stasjonen`} />
      </div>

      {/* Interest split */}
      <div className="bg-amber-deep/30 border border-amber/15 p-5 mb-10" data-testid="survey-interest-split">
        <h2 className="font-display text-lg font-bold tracking-tight mb-4">Interesse for Pendli (av de med plass)</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {["yes", "yes_if_easy", "unsure", "no"].map((k) => (
            <div key={k} className="border border-amber/15 p-4">
              <div className="text-xs text-cream/60 mb-1">{LABELS.pendli_interest[k]}</div>
              <div className="font-display text-2xl font-extrabold text-amber">{stats?.interest?.[k] ?? 0}</div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-cream/40 mt-4">
          Potensiell vert = har privat plass OG svarer «Ja, gjerne» eller «Ja, hvis det er enkelt nok». Rådata beholdes uendret.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-amber-deep/40 border border-amber/15 p-4 md:p-5 mb-8" data-testid="survey-filters">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          <Select testId="filter-surveyor" label="Kartlegger" value={filters.surveyor} onChange={setF("surveyor")}
            options={[{ v: "", l: "Alle" }, ...SURVEYORS.map((s) => ({ v: s.v, l: s.l }))]} />
          <Select testId="filter-sector" label="Sektor" value={filters.sector} onChange={setF("sector")}
            options={[{ v: "", l: "Alle" }, ...SECTORS.map((s) => ({ v: s.v, l: s.l }))]} />
          <Select testId="filter-distance" label="Gangavstand" value={filters.walking_distance} onChange={setF("walking_distance")}
            options={[{ v: "", l: "Alle" }, ...Object.entries(LABELS.walking_distance).map(([v, l]) => ({ v, l }))]} />
          <Select testId="filter-interest" label="Interesse" value={filters.pendli_interest} onChange={setF("pendli_interest")}
            options={[{ v: "", l: "Alle" }, ...Object.entries(LABELS.pendli_interest).map(([v, l]) => ({ v, l }))]} />
          <Select testId="filter-consent" label="Kan kontaktes" value={filters.contact_permission} onChange={setF("contact_permission")}
            options={[{ v: "", l: "Alle" }, { v: "yes", l: "Ja" }, { v: "no", l: "Nei" }]} />
          <Select testId="filter-potential" label="Potensiell vert" value={filters.potential_host} onChange={setF("potential_host")}
            options={[{ v: "", l: "Alle" }, { v: "true", l: "Kun potensielle" }]} />
          <label className="block">
            <span className="block text-[10px] font-bold tracking-widest uppercase text-amber/70 mb-1.5">Gate</span>
            <input data-testid="filter-street" value={filters.street} onChange={(e) => setF("street")(e.target.value)} placeholder="Søk gate"
              className="w-full h-9 bg-ink border border-amber/25 focus:border-amber text-sm text-cream px-2 placeholder:text-cream/30" />
          </label>
          <label className="block">
            <span className="block text-[10px] font-bold tracking-widest uppercase text-amber/70 mb-1.5">Fra dato</span>
            <input type="date" data-testid="filter-date-from" value={filters.date_from} onChange={(e) => setF("date_from")(e.target.value)}
              className="w-full h-9 bg-ink border border-amber/25 focus:border-amber text-sm text-cream px-2" />
          </label>
          <label className="block">
            <span className="block text-[10px] font-bold tracking-widest uppercase text-amber/70 mb-1.5">Til dato</span>
            <input type="date" data-testid="filter-date-to" value={filters.date_to} onChange={(e) => setF("date_to")(e.target.value)}
              className="w-full h-9 bg-ink border border-amber/25 focus:border-amber text-sm text-cream px-2" />
          </label>
          <div className="flex items-end">
            <button onClick={() => setFilters(EMPTY_FILTERS)} data-testid="filter-reset"
              className="h-9 px-4 border border-amber/30 text-cream/70 hover:text-amber text-xs font-bold tracking-widest uppercase">
              Nullstill
            </button>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5 mb-10" data-testid="survey-breakdowns">
        {Object.keys(FIELD_TITLES).map((f) => (
          <Breakdown key={f} field={f} data={stats?.breakdowns?.[f]} />
        ))}
      </div>

      {error && <div className="bg-orange/10 border border-orange p-4 text-orange mb-6 text-sm" data-testid="survey-error">{error}</div>}

      {/* Raw table */}
      <h2 className="font-display text-xl font-bold tracking-tight mb-4">Alle svar</h2>
      <div className="border border-amber/15 overflow-x-auto" data-testid="survey-table">
        <table className="w-full text-sm min-w-[1100px]">
          <thead className="bg-amber-deep/60 text-amber/80 text-xs font-bold tracking-widest uppercase">
            <tr>
              {["Dato", "Kartlegger", "Sektor", "Gate", "Plass", "Avstand", "Ledig", "Interesse", "Hinder", "Inntekt", "Bestemmer", "Kontakt"].map((h) => (
                <th key={h} className="text-left px-3 py-3 font-bold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={12} className="px-4 py-10 text-center text-cream/50" data-testid="survey-loading">Laster...</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={12} className="px-4 py-14 text-center text-cream/50" data-testid="survey-empty">Ingen svar for dette filteret.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} onClick={() => setDetail(r)} data-testid={`survey-row-${r.id}`}
                className={`border-t border-amber/10 hover:bg-amber/5 cursor-pointer transition-colors ${isPotentialHost(r) ? "bg-amber/[0.04]" : ""}`}>
                <td className="px-3 py-3 text-cream/70 whitespace-nowrap">{new Date(r.created_at).toLocaleString("nb-NO", { dateStyle: "short", timeStyle: "short" })}</td>
                <td className="px-3 py-3">{shortLabel("surveyor", r.surveyor)}</td>
                <td className="px-3 py-3 text-cream/70">{shortLabel("sector", r.sector)}</td>
                <td className="px-3 py-3 text-cream/70">{r.street}</td>
                <td className="px-3 py-3">{shortLabel("parking_type", r.parking_type)}</td>
                <td className="px-3 py-3 text-cream/70">{shortLabel("walking_distance", r.walking_distance)}</td>
                <td className="px-3 py-3 text-cream/70">{shortLabel("weekday_availability", r.weekday_availability)}</td>
                <td className="px-3 py-3 text-amber">{shortLabel("pendli_interest", r.pendli_interest)}</td>
                <td className="px-3 py-3 text-cream/70">{shortLabel("main_barrier", r.main_barrier)}</td>
                <td className="px-3 py-3 text-cream/70">{shortLabel("acceptable_monthly_income", r.acceptable_monthly_income)}</td>
                <td className="px-3 py-3 text-cream/70">{shortLabel("parking_decision_maker", r.parking_decision_maker)}</td>
                <td className="px-3 py-3 text-cream/70 whitespace-nowrap">
                  {r.contact_permission === "yes"
                    ? <span>{r.contact_name}<span className="text-cream/40"> · {r.contact_phone || r.contact_email}</span></span>
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-cream/40 mt-4">Viser {rows.length} svar · klikk en rad for full undersøkelse</p>

      <DetailModal row={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
